<template>
  <v-app>
    <v-app-bar
      app
      clipped-left
      clipped-right
    >
      <v-app-bar-nav-icon
        v-show="$auth.isAuthenticated"
        @click.stop="navigation = !navigation"
      />
      <div class="d-flex align-center">
        <a
          target="_blank"
          href="//www.mla.com.au/marketing-beef-and-lamb/meat-standards-australia/"
        >
          <v-img
            src="../public/img/icons/icon-120x120.png"
            alt="MSA"
            class="shrink mr-2"
            contain
            transition="scale-transition"
            width="40"
          />
        </a>
        <a
          target="_blank"
          href="//www.mla.com.au/"
        >
          <v-img
            src="../public/img/icons/logo_mla_t.png"
            alt="MLA"
            class="shrink mr-2"
            contain
            transition="scale-transition"
            width="80"
          />
        </a>
        {{ $route.meta.title }}
      </div>

      <v-spacer></v-spacer>

      <template v-if="$auth.isAuthenticated">
        <div class="grow text-right mr-5">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
              >
                <v-icon v-show="isMemberAdmin">supervisor_account</v-icon>
                <v-icon v-show="!isMemberAdmin">person</v-icon>
                {{ $auth.userProfile.name }}
              </span>
            </template>
            <span v-show="isMemberAdmin">You are an administrator for {{ selectedMemberNumber }}.</span>
            <span v-show="!isMemberAdmin">
              You are a restricted user for {{ selectedMemberNumber }}, access to some features may be restricted.<br/>
              Request administrator access from your MSA Account holder to access these features.
            </span>
          </v-tooltip>
        </div>
        <v-select
          label="MSA Member"
          v-if="$auth.userProfile.members && $auth.userProfile.members.length"
          :items="$auth.userProfile.members"
          :disabled="$auth.userProfile.members.length <= 1"
          item-text="memberNumber"
          :value="selectedMemberNumber"
          @input="selectMember"
          hide-details
          class="shrink mr-5"
        />
        <v-btn
          color="primary"
          depressed
          @click="signOut"
        >
          Log out
        </v-btn>
      </template>

      <v-btn
        v-else
        color="primary"
        depressed
        @click="signIn"
      >
        Log in
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-if="$auth.isAuthenticated"
      v-model="navigation"
      clipped
      app
      :width="300"
    >
      <div>
        <v-subheader>myMSA</v-subheader>
        <v-list-item
          :to="{ name: 'Menu' }"
        >
          <v-list-item-icon>
            <v-icon>dashboard</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>myMSA Menu</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
      <div>
        <v-subheader>My Details</v-subheader>
        <user-navigation />
      </div>
      <div v-show="isElevated">
        <v-divider />
        <v-subheader>MSA Administration</v-subheader>
        <admin-navigation />
      </div>
    </v-navigation-drawer>

    <v-main>
      <router-view
        @error="onError"
        @info="onInfo"
        @debug="onDebug"
      >
      </router-view>
    </v-main>

    <v-snackbar
      v-model="showError"
      color="red"
    >
      {{ previousError }}

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="showInfo"
      color="primary"
      timeout="10000"
    >
      {{ previousInfo }}

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showInfo = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-overlay
      :value="showLogInPrompt"
    >
      <v-card>
        <v-card-text class="text-center">
          <p class="text-title">Your session has expired. Please log back in to continue your session.</p>
          <v-btn
            color="primary"
            @click="resumeSession"
            large
            block
          >
            Log back in
          </v-btn>
          <p>
            or
          </p>
          <v-btn
            color="warning"
            @click="signOut"
            x-small
            block
            outlined
          >
            Log out
          </v-btn>
        </v-card-text>
      </v-card>
    </v-overlay>

    <v-overlay
      :value="showLoggingInOverlay"
    >
      <div class="text-center">
        <v-progress-circular
          size="64"
          indeterminate
        />
        <p class="text-caption text-center mt-3">
          Logging you in. If the log in prompt does not appear, please ensure pop-ups are enabled.
        </p>
      </div>
    </v-overlay>

    <v-footer
      fixed
      app
      padless
      height="32"
    >
      <v-container>
        <v-row row wrap>
          <v-col justify="center" class="py-0 text-center">
            myMSA Members version {{ buildVersion || 'indev' }} - &copy; {{ new Date().getFullYear() }} |
            <a
              target="_blank"
              href="//www.mla.com.au/General/Privacy"
              >MLA Privacy Policy</a
            >
          </v-col>
          <v-col justify="right" class="py-0 text-right">
            <report-problem
              @error="onError"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<style>
tbody tr:nth-of-type(odd) {
  background-color: #F8F8F8;

  @media print {
    background-color: #F5F5F5;
    color: black !important;
  }
}

thead tr th, th .v-input, th .v-label {
  background-color: #007549;
  color: white !important;

  @media print {
    background-color: #F5F5F5;
    color: black !important;
  }
}
</style>

<script>
import log from 'loglevel'
import { mapState, mapGetters } from 'vuex'

import { isElevated } from './auth'
import { mutations, getters } from './store'
import { getEnv } from './utilities/env'
import AdminNavigation from './components/AdminNavigation.vue'
import UserNavigation from './components/UserNavigation.vue'
import ReportProblem from './components/ReportProblem.vue'

const showNavigationOnBreakpoints = ['md', 'lg', 'xl']

export default {
  name: 'App',

  components: {
    AdminNavigation,
    UserNavigation,
    ReportProblem
  },

  data: () => ({
    showError: false,
    showInfo: false,
    previousError: null,
    previousInfo: null,
    navigation: false,
    initialNavigationSet: false,
    showLogInPrompt: false,
    showLoggingInOverlay: false
  }),

  computed: {
    isElevated,
    ...mapState([
      'selectedMemberNumber'
    ]),
    ...mapGetters([
      getters.IS_ADMIN_FOR_REGISTRATION
    ]),
    isMemberAdmin  () {
      return this[getters.IS_ADMIN_FOR_REGISTRATION]
    },
    buildVersion () {
      return getEnv('VUE_APP_BUILD_VERSION')
    }
  },

  methods: {
    onError (error) {
      log.error(error)
      this.previousError = 'An unexpected error occurred. Please try again. If the error persists, please contact MLA support.'
      if (error instanceof Error) this.previousError = error.message
      this.showError = true
    },
    onInfo (info) {
      if (!info) return
      this.previousInfo = info
      this.showInfo = true
    },
    onDebug (debug) {
      if (getEnv('NODE_ENV') !== 'development') return
      log.debug(debug)
    },
    async signOut () {
      this.$router.push({ path: '/' })
      await this.$auth.signOut()
      this.showLogInPrompt = false
    },
    selectMember (value) {
      this.$store.commit(mutations.SET_SELECTED_MEMBER_NUMBER, value)
    },
    setInitialNavigation () {
      if (!this.initialNavigationSet) {
        this.navigation = showNavigationOnBreakpoints.includes(this.$vuetify.breakpoint.name) &&
          this.$auth.isAuthenticated
      }
      // Prevent re-opening a menu closed by the user when their tokens are refreshed
      this.initialNavigationSet = true
    },
    async signIn () {
      this.showLoggingInOverlay = true
      // Re-compute navigation after signing back in
      this.initialNavigationSet = false

      // Wait for sign in and navigate to membership details page if necessary
      try {
        await this.$auth.signIn()
        const isCurrentRoutePublic = this.$route.meta?.authName !== this.$auth.authName
        if (isCurrentRoutePublic) this.$router.push({ name: 'Membership' })
      } catch (e) {
        log.warn('Failed to sign in.', e)
      } finally {
        this.showLoggingInOverlay = false
      }
    },
    async resumeSession () {
      this.showLoggingInOverlay = true
      try {
        await this.$auth.signIn()
      } catch (e) {
        log.error('Failed to resume session.', e)
      } finally {
        this.showLoggingInOverlay = false
      }
    }
  },

  created () {
    // When access token expires, show prompt
    const $vm = this
    this.$auth.events.addAccessTokenExpired(() => {
      $vm.showLogInPrompt = true
    })

    // After re-auth, hide prompt
    this.$auth.events.addUserLoaded(() => {
      $vm.showLogInPrompt = false
      $vm.showLoggingInOverlay = false
      $vm.setInitialNavigation()
    })
  },

  watch: {
    '$auth.isAuthenticated' (isAuthenticated) {
      this.navigation = isAuthenticated && showNavigationOnBreakpoints.includes(this.$vuetify.breakpoint.name)
    }
  },

  mounted () {
    this.setInitialNavigation()
  }
}
</script>
